<template>
  <div class="certificates-databank">
    <nav class="vp-crumbs" :aria-label="$t('databank.certificates.title')">
      <router-link to="/databank">{{ $t('databank.title') }}</router-link>
      <span class="vp-crumbs-sep">/</span>
      <span>{{ $t('databank.certificates.title') }}</span>
    </nav>

    <div class="vp-page-head">
      <div class="vp-min-w-0">
        <h1 class="vp-page-title">{{ $t('databank.certificates.title') }}</h1>
        <div class="vp-page-sub">
          <i class="ph-duotone ph-certificate"></i>
          <span>{{ $t('databank.certificates.description') }}</span>
        </div>
      </div>
      <div class="vp-page-actions">
        <button type="button" class="vp-btn vp-btn-ghost" :disabled="isLoadingData" @click="loadCertificates()">
          <i class="ph-duotone ph-arrow-clockwise" :class="{ 'vp-spin': isLoadingData }"></i>
          {{ $t('common.refresh') }}
        </button>
        <button type="button" class="vp-btn vp-btn-primary" @click="openAddModal">
          <i class="ph-duotone ph-plus"></i>{{ $t('databank.certificates.addTitle') }}
        </button>
      </div>
    </div>

    <div v-if="error" class="vp-note vp-note--danger vp-stack-bottom">
      <i class="ph-duotone ph-warning-circle"></i>
      <div class="vp-min-w-0">{{ error }}</div>
      <button type="button" class="vp-note-dismiss" :aria-label="$t('common.close')" @click="error = null">
        <i class="ph-duotone ph-x"></i>
      </button>
    </div>

    <div v-if="success" class="vp-note vp-note--success vp-stack-bottom">
      <i class="ph-duotone ph-check-circle"></i>
      <div class="vp-min-w-0">{{ success }}</div>
      <button type="button" class="vp-note-dismiss" :aria-label="$t('common.close')" @click="success = null">
        <i class="ph-duotone ph-x"></i>
      </button>
    </div>

    <div class="vp-filterbar">
      <div class="vp-field vp-field--grow">
        <i class="ph-duotone ph-magnifying-glass vp-field-icon"></i>
        <input
          v-model="searchQuery"
          type="search"
          class="vp-input"
          :placeholder="$t('common.actions.search')"
          :aria-label="$t('common.actions.search')"
        />
      </div>
      <div class="vp-filterbar-end">
        <button
          v-if="searchQuery"
          type="button"
          class="vp-btn vp-btn-ghost vp-btn-sm"
          :title="$t('common.clear')"
          @click="clearSearch"
        >
          <i class="ph-duotone ph-x"></i>{{ $t('common.clear') }}
        </button>
      </div>
    </div>

    <section class="vp-card">
      <div class="vp-card-head">
        <h6 class="vp-card-title">
          {{ $t('databank.certificates.existingRecords') }}
          <span class="vp-chip">{{ $formatInteger(filteredCertificates.length) }}</span>
        </h6>
      </div>

      <div v-if="isLoadingData" class="vp-loading"><span class="spinner-border spinner-border-sm"></span></div>

      <div v-else-if="filteredCertificates.length" class="vp-table-wrap">
        <table class="vp-table" :aria-label="$t('databank.certificates.title')">
          <thead>
            <tr>
              <th>{{ $t('databank.certificates.codeFunction') }}</th>
              <th>{{ $t('databank.certificates.nameFunction') }}</th>
              <th class="text-end">{{ $t('databank.certificates.intervalFunction') }}</th>
              <th>{{ $t('common.createdAt') }}</th>
              <th class="text-end">{{ $t('common.actionsLabel') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="certificate in filteredCertificates" :key="certificate.id">
              <td>
                <span v-if="certificate.code" class="vp-mono">{{ certificate.code }}</span>
                <span v-else class="vp-text-muted">—</span>
              </td>
              <td class="vp-cell-strong">{{ certificate.name }}</td>
              <td class="text-end vp-num">
                <span v-if="certificate.interval">{{ certificate.interval }}</span>
                <span v-else class="vp-text-muted">—</span>
              </td>
              <td class="vp-num text-nowrap">{{ formatDate(certificate.createdAt) }}</td>
              <td class="text-end">
                <div class="vp-row-actions">
                  <button
                    type="button"
                    class="vp-icon-btn"
                    :title="$t('common.edit')"
                    @click="editCertificate(certificate)"
                  >
                    <i class="ph-duotone ph-pencil-simple"></i>
                  </button>
                  <button
                    type="button"
                    class="vp-icon-btn vp-icon-btn--danger"
                    :title="$t('common.delete')"
                    @click="deleteCertificate(certificate.id)"
                  >
                    <i class="ph-duotone ph-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="vp-empty">
        <i class="ph-duotone ph-certificate vp-empty-icon"></i>
        <div class="vp-empty-title">{{ $t('databank.certificates.title') }}</div>
        <div class="vp-empty-text">{{ $t('databank.certificates.noRecords') }}</div>
      </div>
    </section>

    <!-- Add / edit certificate -->
    <div class="modal fade" id="certificateModal" tabindex="-1" aria-labelledby="certificateModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content vp-modal">
          <div class="vp-modal-head">
            <h5 class="vp-modal-title" id="certificateModalLabel">
              <i class="ph-duotone ph-certificate"></i>
              {{ isEditing ? $t('databank.certificates.editTitle') : $t('databank.certificates.addTitle') }}
            </h5>
            <button type="button" class="vp-modal-close" data-bs-dismiss="modal" :aria-label="$t('common.close')">
              <i class="ph-duotone ph-x"></i>
            </button>
          </div>

          <div class="vp-modal-body">
            <form class="vp-form-grid" @submit.prevent="submitForm">
              <div class="vp-form-field">
                <label class="vp-label" for="certificateCode">
                  {{ $t('databank.certificates.codeFunction') }}
                </label>
                <input
                  id="certificateCode"
                  v-model="form.code"
                  type="text"
                  class="vp-control vp-mono"
                  :placeholder="$t('databank.certificates.codePlaceholder')"
                />
              </div>

              <div class="vp-form-field">
                <label class="vp-label" for="certificateName">
                  {{ $t('databank.certificates.nameFunction') }} <span class="vp-label-req">*</span>
                </label>
                <input
                  id="certificateName"
                  v-model="form.name"
                  type="text"
                  class="vp-control"
                  :class="{ 'is-invalid': v$.name.$error }"
                  :placeholder="$t('databank.certificates.namePlaceholder')"
                  required
                />
                <div v-if="v$.name.$error" class="vp-help vp-help--error">
                  {{ v$.name.$errors[0].$message }}
                </div>
              </div>

              <div class="vp-form-field vp-form-field--full">
                <label class="vp-label" for="certificateInterval">
                  {{ $t('databank.certificates.intervalFunction') }}
                </label>
                <CultureNumberInput
                  id="certificateInterval"
                  v-model="form.interval"
                  :decimal-places="0"
                  :use-grouping="false"
                  :min="1"
                  :expand-on-focus="false"
                  class="vp-control"
                  :class="{ 'is-invalid': v$.interval.$error }"
                  :placeholder="$t('databank.certificates.intervalPlaceholder')"
                />
                <div v-if="v$.interval.$error" class="vp-help vp-help--error">
                  {{ v$.interval.$errors[0].$message }}
                </div>
              </div>
            </form>
          </div>

          <div class="vp-modal-foot">
            <button type="button" class="vp-btn vp-btn-ghost" :disabled="isLoading" @click="handleSave(false)">
              <span v-if="isLoading" class="spinner-border spinner-border-sm"></span>
              <i v-else class="ph-duotone ph-floppy-disk"></i>
              {{ isLoading ? $t('common.saving') : $t('common.save') }}
            </button>
            <button type="button" class="vp-btn vp-btn-primary" :disabled="isLoading" @click="handleSave(true)">
              <span v-if="isLoading" class="spinner-border spinner-border-sm"></span>
              <i v-else class="ph-duotone ph-check"></i>
              {{ isLoading ? $t('common.savingAndClosing') : $t('common.saveAndClose') }}
            </button>
            <button type="button" class="vp-btn vp-btn-ghost" data-bs-dismiss="modal">
              {{ $t('common.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { required, integer, minValue } from '@vuelidate/validators';
import certificateService from '@/services/certificateService';
import { Modal } from 'bootstrap';
import CultureNumberInput from '@/components/CultureNumberInput.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const error = ref(null);
const success = ref(null);
const isLoading = ref(false);
const isLoadingData = ref(false);
const isEditing = ref(false);
const editingId = ref(null);
const searchQuery = ref('');
let certificateModal = null;

const form = ref({
  code: '',
  name: '',
  interval: null
});

const certificates = ref([]);

const currentUser = computed(() => store.getters['auth/currentUser']);

// Validation rules
const rules = computed(() => ({
  name: { required },
  interval: {
    integer,
    minValue: minValue(1)
  }
}));

const v$ = useVuelidate(rules, form);

// Use certificates directly since search is handled by API
const filteredCertificates = computed(() => {
  return certificates.value;
});

// Watch for search query changes
let searchTimeout;
watch(searchQuery, (newQuery) => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    if (newQuery.trim()) {
      loadCertificates(newQuery.trim());
    } else {
      loadCertificates();
    }
  }, 500); // 500ms debounce
});

onMounted(() => {
  // Check if user is super admin
  if (!currentUser.value?.isSuperAdmin) {
    store.dispatch('notify', {
      message: t('common.notification.unauthorized'),
      type: 'error'  
    });
    router.push('/databank');
  }
  
  // Initialize modal
  const modalElement = document.getElementById('certificateModal');
  if (modalElement) {
    certificateModal = new Modal(modalElement, { focus: false });
  }
  
  loadCertificates();
});

const loadCertificates = async (searchText = '') => {
  isLoadingData.value = true;
  
  try {
    let response;
    
    // Handle case where event object is passed instead of string
    const searchQuery = typeof searchText === 'string' ? searchText : '';
    
    if (searchQuery) {
      // Use search API when there's a search query
      response = await certificateService.searchCertificates({
        searchText: searchQuery,
        page: 0,
        pageSize: 100
      });
    } else {
      // Load all certificates
      response = await certificateService.getCertificates();
    }
    
    if (response && Array.isArray(response)) {
      certificates.value = response.map(cert => ({
        id: cert.id,
        code: cert.code,
        name: cert.name,
        interval: cert.interval || cert.interval,
        createdAt: cert.creationTime || cert.createdAt
      }));
    } else if (response && response.items) {
      // Handle paginated response
      certificates.value = response.items.map(cert => ({
        id: cert.id,
        code: cert.code,
        name: cert.name,
        interval: cert.interval || cert.interval,
        createdAt: cert.creationTime || cert.createdAt
      }));
    } else {
      certificates.value = [];
    }
    
  } catch (err) {
    console.error('Error loading certificates:', err);
    error.value = t('databank.certificates.errors.loadError');
    
    store.dispatch('notify', {
      message: error.value,
      type: 'error'
    });
  } finally {
    isLoadingData.value = false;
  }
};

const openAddModal = () => {
  resetForm();
  if (certificateModal) {
    certificateModal.show();
  }
};

const handleSave = async (shouldClose = false) => {
  // Validate form
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;
  
  isLoading.value = true;
  let savedSuccessfully = false;
  let savedCertificate = null;
  error.value = null;
  success.value = null;
  
  try {
    const certificateData = {
      code: form.value.code || null,
      name: form.value.name,
      interval: form.value.interval
    };
    
    if (isEditing.value) {
      // Update existing certificate
      const response = await certificateService.updateCertificate(editingId.value, certificateData);
      
      if (response) {
        savedCertificate = response;
        // Update local array
        const index = certificates.value.findIndex(c => c.id === editingId.value);
        if (index !== -1) {
          certificates.value[index] = {
            ...certificates.value[index],
            code: response.code,
            name: response.name,
            interval: response.interval || response.interval
          };
        }
        success.value = t('databank.certificates.updateSuccess');
      }
    } else {
      // Create new certificate
      const response = await certificateService.createCertificate(certificateData);
      
      if (response) {
        savedCertificate = response;
        // Add to local array
        const newCertificate = {
          id: response.id,
          code: response.code,
          name: response.name,
          interval: response.interval || response.interval,
          createdAt: response.creationTime || new Date()
        };
        certificates.value.unshift(newCertificate);
        success.value = t('databank.certificates.createSuccess');
      }
    }
    
    savedSuccessfully = true;
    
    store.dispatch('notify', {
      message: success.value,
      type: 'success'
    });
    
    // If closing, hide the modal
    if (shouldClose && certificateModal) {
      certificateModal.hide();
      resetForm();
    }
    
  } catch (err) {
    console.error('Submit error:', err);
    error.value = t('databank.certificates.errors.saveError');
    
    store.dispatch('notify', {
      message: error.value,
      type: 'error'
    });
  } finally {
    isLoading.value = false;
  }
  
  // Post-Save Actions (only if save was successful and modal is staying open)
  if (savedSuccessfully && !shouldClose) {
    if (!isEditing.value && savedCertificate) {
      // If creating and NOT closing, switch to edit mode for the newly created certificate
      form.value = {
        code: savedCertificate.code || '',
        name: savedCertificate.name || '',
        interval: savedCertificate.interval || null
      };
      isEditing.value = true;
      editingId.value = savedCertificate.id;
    }
  }
};

const editCertificate = (certificate) => {
  form.value = {
    code: certificate.code || '',
    name: certificate.name || '',
    interval: certificate.interval || null
  };
  isEditing.value = true;
  editingId.value = certificate.id;
  
  if (certificateModal) {
    certificateModal.show();
  }
};

const deleteCertificate = async (id) => {
  if (!confirm(t('databank.certificates.confirmDelete'))) return;
  
  try {
    await certificateService.deleteCertificate(id);
    
    certificates.value = certificates.value.filter(c => c.id !== id);
    
    store.dispatch('notify', {
      message: t('databank.certificates.deleteSuccess'),
      type: 'success'
    });
    
  } catch (err) {
    console.error('Delete error:', err);
    store.dispatch('notify', {
      message: t('databank.certificates.errors.deleteError'),
      type: 'error'
    });
  }
};

const resetForm = () => {
  form.value = {
    code: '',
    name: '',
    interval: null
  };
  isEditing.value = false;
  editingId.value = null;
  v$.value.$reset();
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString();
};

const clearSearch = () => {
  searchQuery.value = '';
};
</script>
